import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get, throttle } from "lodash";
import StickyBanner from "../../molecules/sticky-banner";
import StickyNavbar from "../../molecules/sticky-navbar";
import { setMakeSecondaryApiCallsTriggered, setUserDetails, setIsLogin } from "../../store/actions";
import { addTokensToLocalStorage, getUserDetails } from "../../helper/utils";
import GoogleOneTapSignInUp from "../../atoms/google-one-tap";
import { GoogleOAuthProvider, googleLogout } from "@react-oauth/google";
import DesktopHeader from "./new-pv-header/desktop-header";
import MobileHeader from "./new-pv-header/mobile-header";
import MyAccount from "../../atoms/my-account";
import Modal from "../../atoms/modal";
import { OPEN_HAMBURGER_MENU, OPEN_SEARCHBAR } from "../../store/actions";
// import EPaperBanner from "../../atoms/epaper-banner";
// import NewIframeTopBanner from "../../atoms/new-iframe-top-banner";
// import ElectionFloatingWidget from "../../atoms/election-floating-widget";
import { pushPageDataLayer, addSignUpLoginToDataLayer, addLogoutToDataLayer, addAuthTrackingToDataLayer } from "../../helper/datalayer-utils";
import { createScrollHandler } from "./useScrollBehavior";

const NavigationSchema = () => {
  // Get navigation menu data from redux
  const navigationMenu = useSelector((state) =>
    get(state, ["qt", "data", "navigationMenu"], {})
  );

  const schema = useMemo(() => {
    const primaryMenus = navigationMenu?.primaryMenus || [];
    const secondaryMenus = navigationMenu?.secondaryMenus || [];

    // Merge both arrays
    const allMenus = [...primaryMenus, ...secondaryMenus];

    // Helper function to build schema recursively
    const buildMenuSchema = (item) => {
      if (!item?.title || !item?.url) return null;

      const menuSchema = {
        "@type": "SiteNavigationElement",
        name: item.title,
        url: item.url
      };

      return menuSchema;
    };

    // Build navigation schema
    const hasPart = allMenus
      .map(buildMenuSchema)
      .filter(Boolean);

    return {
      "@context": "https://schema.org",
      "@type": "SiteNavigationElement",
      name: "Prajavani Main Navigation",
      hasPart
    };
  }, [navigationMenu]);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema)
      }}
    />
  );
};

const Header = () => {
  const dispatch = useDispatch();
  const userdetails = useSelector((state) => get(state, ["user", "user"], {}));
  const pageType = useSelector((state) => get(state, ["qt", "pageType"]));
  const pageData = useSelector((state) => get(state, ["qt", "data"], []));
  const isHamburgerMenuOpen = useSelector((state) => get(state, ["isHamburgerMenuOpen"], false));
  const bottomNavBarMenus = useSelector((state) =>
    get(state, ["qt", "data", "navigationMenu", "bottomNavBar"], [])
  );

  const makeSecondaryApiCallsDelay = 2750;
  const [makeSecondaryApiCalls, setMakeSecondaryApiCalls] = useState(false);
  const [isMobile, setMobile] = useState(true);
  const [loadOneTap, setGoogleOneTap] = useState(false);
  const [redirectURL, setRedirectUrl] = useState("");
  const [showStickyNavbar, setShowStickyNavbar] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState("up");
  const [showPrimaryHeader, setShowPrimaryHeader] = useState(true);
  const [showSecondaryHeader, setShowSecondaryHeader] = useState(true);
  const [showAdBanner, setShowAdBanner] = useState(false);
  const [adClosed, setAdClosed] = useState(false);
  const [showDesktopSecondaryHeader, setShowDesktopSecondaryHeader] = useState(true);
  const [showAccountModal, setShowAccountModal] = useState(false);

  const openAccountModal = () => {
    setShowAccountModal(true);
  };

  const closeAccountModal = () => {
    setShowAccountModal(false);
  };

  const GoogleClientId = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "google_client_id"], "")
  );
  const ssoEndpoint = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "sso_endpoint"], null)
  );

  const dataLayerPushedRef = useRef(null);
  const trackingTimerRef = useRef(null);
  const trackingDataRef = useRef({ pageType, userdetails, pageData });

  useEffect(() => {
    trackingDataRef.current = { pageType, userdetails, pageData };
  }, [pageType, userdetails, pageData]);

  useEffect(() => {
    if (typeof window === "undefined" || !pageType) return;
    const contentId = pageData?.story?.id || pageData?.section?.id || pageData?.tag?.id || "";
    const userId = userdetails?.userData?.id || userdetails?.id || "anonymous";
    const currentRouteId = `${pageType}-${contentId}-${userId}`;

    if (dataLayerPushedRef.current !== currentRouteId) {
      dataLayerPushedRef.current = currentRouteId;
      if (trackingTimerRef.current) clearTimeout(trackingTimerRef.current);

      trackingTimerRef.current = setTimeout(() => {
        const { pageType: pt, userdetails: ud, pageData: pd } = trackingDataRef.current;
        pushPageDataLayer(pt, ud, pd);
      }, 150);
    }
  }, [pageType, pageData, userdetails]);

  const toggleHandler = () => {
    dispatch({
      type: OPEN_HAMBURGER_MENU,
      isHamburgerMenuOpen: !isHamburgerMenuOpen
    });

    dispatch({
      type: OPEN_SEARCHBAR,
      isSearchBarOpen: false
    });
  };

  const getScroll = createScrollHandler({
    lastScrollY,
    makeSecondaryApiCalls,
    adClosed,
    setMakeSecondaryApiCalls,
    setShowPrimaryHeader,
    setShowSecondaryHeader,
    setShowAdBanner,
    setShowStickyNavbar,
    setShowDesktopSecondaryHeader,
    setScrollDirection,
    setLastScrollY,
    showPrimaryHeader,
    showSecondaryHeader,
    showAdBanner,
    showStickyNavbar,
    showDesktopSecondaryHeader
  });

  useEffect(() => {
    setRedirectUrl(window.location.href);

    if (window && window.innerWidth < 1025) {
      setMobile(true);
    } else {
      setMobile(false);
      setMakeSecondaryApiCalls(true);
    }
    if (window) {
      const searchQuery = window.location.search;
      const accessTokenIndex = searchQuery.indexOf("?access_token");
      const output = accessTokenIndex !== -1 ? searchQuery.substring(accessTokenIndex) : searchQuery;
      const searchParams = new URLSearchParams(output);
      const access_token = searchParams.get("access_token");
      const refresh_token = searchParams.get("refresh_token");
      const userLocalStorage = localStorage.getItem("user");
      const stored_access_token = localStorage.getItem("access_token");
      const stored_refresh_token = localStorage.getItem("refresh_token");

      if (stored_refresh_token && stored_access_token) {
        dispatch(setIsLogin(true));
      }
      if (access_token && refresh_token) {
        addTokensToLocalStorage(access_token, refresh_token);
        getUserDetails()
          .then((data) => {
            if (data?.status === 401) {
              // Remove an tokens from localStorage
              localStorage.removeItem("access_token");
              localStorage.removeItem("refresh_token");
              localStorage.removeItem("user");
              localStorage.removeItem("commentsToken");

              googleLogout();
              // loggingout user if refresh token expires
              window.location.href = `${ssoEndpoint}/auth/logout?redirectUrl=${window.location.href.split("?")[0]}`;
            } else {
              addSignUpLoginToDataLayer(window.location.href, data);
              sessionStorage.setItem("skip_auth_tracking", "true");
              dispatch(setIsLogin(true));
              dispatch(setUserDetails(data || {}));
              window.localStorage.setItem("user", JSON.stringify(data));

              const searchParam = accessTokenIndex !== -1 ? searchQuery.substring(0, accessTokenIndex) : searchQuery;
              window.history.replaceState({}, "", window.location.pathname + searchParam);
            }
          })
          .catch((err) => {
            console.log(err);
          });
      } else if (userLocalStorage && userLocalStorage !== "undefined" && userdetails?.userData === undefined) {
        if (typeof userLocalStorage === "string" && userLocalStorage.trim() !== "") {
          const parsedUser = JSON.parse(userLocalStorage);
          dispatch(setUserDetails(parsedUser));
        }
      } else {
        setGoogleOneTap(true);
      }
    }
  }, []);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          getScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [adClosed, lastScrollY, makeSecondaryApiCalls, showPrimaryHeader, showSecondaryHeader, showAdBanner, showStickyNavbar, showDesktopSecondaryHeader]);

  useEffect(() => {
    if (adClosed) {
      getScroll();
    }
  }, [adClosed]);

  useEffect(() => {
    makeSecondaryApiCalls &&
      dispatch(setMakeSecondaryApiCallsTriggered(makeSecondaryApiCalls, makeSecondaryApiCallsDelay, isMobile));
  }, [makeSecondaryApiCalls]);

  const isHardcodedHeader = useMemo(() => {
    let headerType = "";
    if (pageType === "section-page" || pageType === "sub-section-page") {
      const customHeader = pageData?.collection?.metadata?.["custom-header"];
      if (customHeader === "cok2025") {
        headerType = "cok2025";
      } else if (customHeader === "festivals") {
        headerType = "festivals";
      } else if (customHeader === "Canara-Robeco") {
        headerType = "Canara-Robeco";
      }
    }
    return headerType;
  }, [pageType, pageData]);

  useEffect(() => {
    if (typeof document !== "undefined" && typeof window !== "undefined") {
      // Skip padding for custom headers - they handle their own spacing
      if (isHardcodedHeader) {
        document.body.style.paddingTop = "0px";
        document.body.style.paddingBottom = "0px";
        return;
      }

      const isMobileView = window.innerWidth < 769;
      if (isMobileView) {
        // Always reserve space for bottom elements to prevent CLS
        // This space is reserved whether elements are visible or not
        let bottomPadding = 0;
        if (showStickyNavbar && showAdBanner) {
          bottomPadding = 120; // navbar + banner
        } else if (showStickyNavbar) {
          bottomPadding = 52; // navbar only
        } else if (showAdBanner) {
          bottomPadding = 68; // banner only
        } else {
          bottomPadding = 0; // nothing visible
        }
        document.body.style.paddingBottom = `${bottomPadding}px`;

        // Reserve space for top headers
        let topPadding = 0;
        if (showPrimaryHeader && showSecondaryHeader) {
          topPadding = 83;
        } else if (showSecondaryHeader) {
          topPadding = 36;
        } else {
          topPadding = 0;
        }
        document.body.style.paddingTop = `${topPadding}px`;
      } else {
        document.body.style.paddingBottom = "0px";
        let desktopTopPadding = 75;
        if (showDesktopSecondaryHeader) {
          desktopTopPadding += 46;
        }
        document.body.style.paddingTop = `${desktopTopPadding}px`;
      }
    }
    return () => {
      if (typeof document !== "undefined") {
        document.body.style.paddingBottom = "0px";
        document.body.style.paddingTop = "0px";
        document.body.style.transition = "";
      }
    };
  }, [showStickyNavbar, showPrimaryHeader, showSecondaryHeader, showDesktopSecondaryHeader, showAdBanner, isHardcodedHeader]);

  const signinHandler = () => {
    if (window) {
      window.dataLayer.push({
        event: "SSO_Page_Open"
      });
    }
    window.location.href = `${ssoEndpoint}/auth?redirectUrl=${redirectURL}`;
  };

  return (
    <GoogleOAuthProvider clientId={GoogleClientId}>
      <NavigationSchema />
      {loadOneTap && <GoogleOneTapSignInUp />}
      <div className="header-hide-tab">
        <DesktopHeader
          signinHandler={signinHandler}
          isHardcodedHeader={isHardcodedHeader}
          showSecondaryHeader={showDesktopSecondaryHeader}
          onAccountHandler={openAccountModal}
        />
      </div>
      <div className="header-hide-dektop">
        <MobileHeader
          signinHandler={signinHandler}
          isHardcodedHeader={isHardcodedHeader}
          showPrimaryHeader={showPrimaryHeader}
          showSecondaryHeader={showSecondaryHeader}
          onAccountHandler={openAccountModal}
        />
      </div>
      {/* {!avoidHomePage && <NewIframeTopBanner/>} */}
      {/* <EPaperBanner utmMedium="topbanner" pageType={pageType} isHardcodedHeader={isHardcodedHeader} /> */}
      {isHardcodedHeader !== "Canara-Robeco" && <StickyBanner isVisible={showAdBanner} hasBottomNav={showStickyNavbar} />}
      <StickyNavbar onMenuToggle={toggleHandler} menuItems={bottomNavBarMenus} isVisible={showStickyNavbar} />
      <Modal onClose={closeAccountModal} isOpen={showAccountModal}>
        {showAccountModal && <MyAccount closeModal={closeAccountModal} />}
      </Modal>
      {/* <ElectionFloatingWidget /> */}
    </GoogleOAuthProvider>
  );
};

export { Header };
